import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Heart, ShoppingBag, ChevronDown, Menu, X } from 'lucide-react';

const categories = [
  {
    name: 'Apparel',
    href: '/collections/apparel',
    subcategories: [
      { name: 'Tops', href: '/collections/apparel?filter=tops' },
      { name: 'Bottoms', href: '/collections/apparel?filter=bottoms' },
      { name: 'Outfit Sets', href: '/collections/apparel?filter=outfit-sets' },
      { name: 'Sweaters', href: '/collections/apparel?filter=sweaters' },
    ],
  },
  {
    name: 'Dresses',
    href: '/collections/dresses',
    subcategories: [
      { name: 'Maxi Dresses', href: '/collections/dresses?filter=maxi' },
      { name: 'Midi Dresses', href: '/collections/dresses?filter=midi' },
      { name: 'Mini Dresses', href: '/collections/dresses?filter=mini' },
    ],
  },
  {
    name: 'One-Pieces',
    href: '/collections/one-pieces',
    subcategories: [
      { name: 'Jumpsuits', href: '/collections/one-pieces?filter=jumpsuits' },
      { name: 'Rompers', href: '/collections/one-pieces?filter=rompers' },
    ],
  },
  {
    name: 'Footwear',
    href: '/collections/footwear',
    subcategories: [
      { name: 'Sandals', href: '/collections/footwear?filter=sandals' },
      { name: 'Boots', href: '/collections/footwear?filter=boots' },
      { name: 'Heels', href: '/collections/footwear?filter=heels' },
      { name: 'Hot Chocolate Design', href: '/collections/hot-chocolate-design' },
    ],
  },
  {
    name: 'Jewelry',
    href: '/collections/jewelry',
    subcategories: [
      { name: 'Necklaces', href: '/collections/jewelry?filter=necklaces' },
      { name: 'Earrings', href: '/collections/jewelry?filter=earrings' },
      { name: 'Bracelets', href: '/collections/jewelry?filter=bracelets' },
      { name: 'Rings', href: '/collections/jewelry?filter=rings' },
    ],
  },
  {
    name: 'Lifestyle',
    href: '/collections/lifestyle-decor',
    subcategories: [],
  },
];

const PreviewNav = () => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);

  const toggleMobileCategory = (name: string) => {
    setMobileExpanded(mobileExpanded === name ? null : name);
  };

  return (
    <nav className="bg-[hsl(30,25%,97%)] border-b border-border sticky top-0 z-50">
      <div className="px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-14 lg:h-16">
          {/* Hamburger — mobile only */}
          <button
            className="lg:hidden p-2 -ml-2 text-foreground"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <span className="font-boho text-xl lg:text-2xl font-bold text-foreground">
              Boho Style Co.
            </span>
          </Link>

          {/* Center nav links — desktop */}
          <div className="hidden lg:flex items-center space-x-1">
            {categories.map((cat) => (
              <div
                key={cat.name}
                className="relative"
                onMouseEnter={() => setOpenDropdown(cat.name)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link
                  to={cat.href}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium tracking-wide uppercase text-foreground hover:text-berry transition-colors"
                >
                  {cat.name}
                  {cat.subcategories.length > 0 && (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </Link>

                {openDropdown === cat.name && cat.subcategories.length > 0 && (
                  <div className="absolute top-full left-0 bg-white shadow-lg border border-border rounded-b-lg min-w-[200px] py-2 z-50">
                    {cat.subcategories.map((sub) => (
                      <Link
                        key={sub.name}
                        to={sub.href}
                        className="block px-5 py-2 text-sm text-foreground hover:bg-muted hover:text-berry transition-colors"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right icons */}
          <div className="flex items-center space-x-3 lg:space-x-4">
            <button className="text-foreground hover:text-berry transition-colors" aria-label="Search">
              <Search className="h-5 w-5" />
            </button>
            <button className="hidden sm:block text-foreground hover:text-berry transition-colors" aria-label="Wishlist">
              <Heart className="h-5 w-5" />
            </button>
            <button className="relative text-foreground hover:text-berry transition-colors" aria-label="Shopping bag">
              <ShoppingBag className="h-5 w-5" />
              <span className="absolute -top-1 -right-1 bg-berry text-berry-foreground text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center" aria-hidden="true">
                0
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile slide-out menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <div className="absolute inset-y-0 left-0 w-[300px] max-w-[85vw] bg-background shadow-xl flex flex-col animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-14 border-b border-border">
              <span className="font-boho text-xl font-bold text-foreground">
                Boho Style Co.
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 -mr-2 text-foreground"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Nav links */}
            <div className="flex-1 overflow-y-auto py-4">
              {categories.map((cat) => (
                <div key={cat.name}>
                  <div className="flex items-center justify-between">
                    <Link
                      to={cat.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex-1 px-6 py-3 text-base font-medium text-foreground active:bg-muted"
                    >
                      {cat.name}
                    </Link>
                    {cat.subcategories.length > 0 && (
                      <button
                        onClick={() => toggleMobileCategory(cat.name)}
                        className="px-4 py-3 text-muted-foreground"
                        aria-label={`Expand ${cat.name}`}
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${
                            mobileExpanded === cat.name ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  {mobileExpanded === cat.name && cat.subcategories.length > 0 && (
                    <div className="bg-muted/50">
                      {cat.subcategories.map((sub) => (
                        <Link
                          key={sub.name}
                          to={sub.href}
                          onClick={() => setMobileOpen(false)}
                          className="block px-10 py-2.5 text-sm text-muted-foreground active:text-berry"
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Bottom actions */}
            <div className="border-t border-border px-6 py-4 flex items-center gap-6">
              <Link to="/favorites" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Heart className="h-5 w-5" /> Favorites
              </Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Search className="h-5 w-5" /> Search
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default PreviewNav;
